import React, { useState, useEffect, useContext } from 'react';
import { useAdminAuth, API } from '../AdminAuthContext';
import CmsContext from '../../context/CmsContext';

const PAGES = [
  { key: 'home', label: 'Home (/)' },
  { key: 'plataforma', label: 'Plataforma (/plataforma)' },
];

const DEFAULT_SEO = {
  home: {
    title: 'Nexus — Gestão Pública Inteligente',
    description: 'Plataforma de gestão pública que automatiza rotinas, reduz burocracias e aumenta a eficiência com decisões inteligentes.',
    og_image: '',
    og_title: '',
    og_description: '',
    keywords: 'gestão pública, nexus, planejamento, controle, eficiência',
  },
  plataforma: {
    title: 'Como funciona o Nexus — Plataforma de Gestão Pública',
    description: 'Conheça os módulos, integrações e o funcionamento completo da plataforma Nexus para gestão pública inteligente.',
    og_image: '',
    og_title: '',
    og_description: '',
    keywords: 'nexus plataforma, módulos, SEI, integração, gestão pública',
  },
};

const Field = ({ label, hint, children }) => (
  <div className="mb-5">
    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">{label}</label>
    {hint && <p className="text-xs text-gray-400 mb-2">{hint}</p>}
    {children}
  </div>
);

const Input = ({ value, onChange, placeholder, maxLen }) => (
  <div>
    <input
      type="text"
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLen}
      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
    />
    {maxLen && (
      <p className={`text-xs mt-1 text-right ${(value || '').length > maxLen * 0.9 ? 'text-orange-500' : 'text-gray-400'}`}>
        {(value || '').length}/{maxLen}
      </p>
    )}
  </div>
);

const Textarea = ({ value, onChange, placeholder, maxLen, rows = 3 }) => (
  <div>
    <textarea
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLen}
      rows={rows}
      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 resize-none"
    />
    {maxLen && (
      <p className={`text-xs mt-1 text-right ${(value || '').length > maxLen * 0.9 ? 'text-orange-500' : 'text-gray-400'}`}>
        {(value || '').length}/{maxLen}
      </p>
    )}
  </div>
);

const SeoPage = () => {
  const { authFetch } = useAdminAuth();
  const { refresh: refreshCms } = useContext(CmsContext);
  const [activePage, setActivePage] = useState('home');
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    setSuccess('');
    fetch(`${API}/api/cms/seo`)
      .then(r => r.json())
      .then(d => setData(Object.keys(d).length === 0 ? DEFAULT_SEO : d))
      .catch(() => setData(DEFAULT_SEO))
      .finally(() => setLoading(false));
  }, []);

  const set = (pageKey, field, val) => {
    setData(prev => ({
      ...prev,
      [pageKey]: { ...(prev[pageKey] || {}), [field]: val },
    }));
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const res = await authFetch('/api/cms/seo', {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Erro ao salvar.');
      setSuccess('SEO salvo! Mudanças aplicadas no site.');
      refreshCms();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const pageData = data?.[activePage] || DEFAULT_SEO[activePage];

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-extrabold text-gray-900">SEO</h2>
          <p className="text-sm text-gray-500 mt-0.5">Meta tags e Open Graph por página</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || loading}
          className="bg-blue-600 text-white text-sm font-semibold px-5 py-2 rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-60 flex items-center gap-2"
        >
          <span className="material-icons text-base">{saving ? 'hourglass_empty' : 'save'}</span>
          {saving ? 'Salvando...' : 'Salvar SEO'}
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-4 text-sm">{error}</div>}
      {success && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl px-4 py-3 mb-4 text-sm">{success}</div>}

      {/* Page tabs */}
      <div className="flex gap-2 mb-6">
        {PAGES.map(p => (
          <button
            key={p.key}
            onClick={() => setActivePage(p.key)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold border-2 transition-all ${
              activePage === p.key ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-0">
          <div className="pb-4 mb-4 border-b border-gray-100">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Tags básicas</p>
            <Field label="Title" hint="Aparece na aba do browser e nos resultados do Google. Ideal: 50–60 caracteres.">
              <Input value={pageData.title} onChange={v => set(activePage, 'title', v)} placeholder="Ex: Nexus — Gestão Pública Inteligente" maxLen={70} />
            </Field>
            <Field label="Meta Description" hint="Resumo nos resultados de busca. Ideal: 140–160 caracteres.">
              <Textarea value={pageData.description} onChange={v => set(activePage, 'description', v)} placeholder="Breve descrição da página..." maxLen={170} rows={2} />
            </Field>
            <Field label="Keywords" hint="Separadas por vírgula. Impacto mínimo em rankings mas útil para organização.">
              <Input value={pageData.keywords} onChange={v => set(activePage, 'keywords', v)} placeholder="gestão pública, nexus, ..." />
            </Field>
          </div>

          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 pt-2">Open Graph (redes sociais)</p>
            <Field label="OG Title" hint="Título no compartilhamento. Deixe vazio para usar o Title acima.">
              <Input value={pageData.og_title} onChange={v => set(activePage, 'og_title', v)} placeholder="(igual ao title se vazio)" maxLen={95} />
            </Field>
            <Field label="OG Description" hint="Descrição no compartilhamento. Deixe vazio para usar a Description acima.">
              <Textarea value={pageData.og_description} onChange={v => set(activePage, 'og_description', v)} placeholder="(igual ao description se vazio)" maxLen={200} rows={2} />
            </Field>
            <Field label="OG Image URL" hint="Imagem exibida ao compartilhar. Recomendado: 1200×630px.">
              <Input value={pageData.og_image} onChange={v => set(activePage, 'og_image', v)} placeholder="https://..." />
            </Field>

            {/* Preview */}
            {(pageData.og_image || pageData.title) && (
              <div className="mt-4 border border-gray-200 rounded-xl overflow-hidden">
                <p className="text-xs font-semibold text-gray-500 px-3 py-2 bg-gray-50 border-b border-gray-100">Pré-visualização de compartilhamento</p>
                {pageData.og_image && (
                  <img src={pageData.og_image} alt="" className="w-full h-40 object-cover" onError={e => { e.target.style.display = 'none'; }} />
                )}
                <div className="p-3 bg-gray-50">
                  <p className="text-xs text-gray-400 mb-0.5">seudominio.com.br</p>
                  <p className="text-sm font-bold text-gray-800">{pageData.og_title || pageData.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{pageData.og_description || pageData.description}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SeoPage;
